/**
 * API base URL for web-backend (e.g. API Gateway base).
 * Set at build time via VITE_API_BASE (injected by app entry or tests).
 * Accepts optional env map for testing.
 */
declare global {
  interface Window {
    __VITE_API_BASE?: string;
  }
}
function readApiBaseFromEnvironment(): string {
  if (
    typeof globalThis !== 'undefined' &&
    (globalThis as { __VITE_API_BASE?: string }).__VITE_API_BASE != null
  ) {
    return (globalThis as { __VITE_API_BASE?: string }).__VITE_API_BASE!;
  }
  return '';
}

/**
 * Backward-compatible guard: old builds/configs may still set api-admin.*
 * after domain consolidation to api.*. Normalize so login redirects remain valid.
 */
function normalizeDeprecatedApiHost(base: string): string {
  return base.replace(/^(https:\/\/)api-admin\./, '$1api.');
}

export function getApiBase(env?: { VITE_API_BASE?: string }): string {
  const base = env?.VITE_API_BASE ?? readApiBaseFromEnvironment();
  if (base != null && base !== '') {
    return normalizeDeprecatedApiHost(base).replace(/\/$/, '');
  }
  return '';
}

/**
 * Validates a return path to prevent open redirects.
 * Rejects values that start with // (protocol-relative) or contain : (e.g. javascript:).
 */
export function isValidReturnPath(path: string): boolean {
  return path.startsWith('/') && !path.startsWith('//') && !path.includes(':');
}

/**
 * Build path for GET /web/admin/auth/login (OAuth start).
 * Passes state=return URL so the callback redirects back to the admin portal (not the API root).
 * If returnTo is provided, it is validated; invalid values fall back to /workspaces.
 */
export function getLoginPath(apiBase: string = getApiBase(), returnTo?: string): string {
  const path = `${apiBase}/web/admin/auth/login`;
  if (typeof window !== 'undefined' && window.location?.origin) {
    const safePath = returnTo && isValidReturnPath(returnTo) ? returnTo : '/workspaces';
    const state = `${window.location.origin}${safePath}`;
    return `${path}?state=${encodeURIComponent(state)}`;
  }
  return path;
}

export interface ListWorkspacesParams {
  teamId?: string;
  q?: string;
  stripeCustomerId?: string;
}

/**
 * Build path for GET /web/admin/workspaces with optional query params.
 */
export function buildListWorkspacesPath(params: ListWorkspacesParams = {}): string {
  const search = new URLSearchParams();
  if (params.teamId) search.set('teamId', params.teamId);
  if (params.q) search.set('q', params.q);
  if (params.stripeCustomerId) search.set('stripeCustomerId', params.stripeCustomerId);
  const qs = search.toString();
  return `/web/admin/workspaces${qs ? `?${qs}` : ''}`;
}

/**
 * Build path for GET /web/admin/workspaces/:teamId (client detail).
 */
export function buildWorkspaceDetailPath(teamId: string): string {
  return `/web/admin/workspaces/${encodeURIComponent(teamId)}`;
}

/**
 * Build path for POST /web/admin/workspaces/:teamId/complimentary-grants.
 */
export function buildComplimentaryGrantPath(teamId: string): string {
  return `/web/admin/workspaces/${encodeURIComponent(teamId)}/complimentary-grants`;
}

/**
 * Build path for GET/PATCH /web/admin/workspaces/:teamId/features/broadcast-mention-tracking.
 */
export function buildBroadcastMentionTrackingPath(teamId: string): string {
  return `/web/admin/workspaces/${encodeURIComponent(teamId)}/features/broadcast-mention-tracking`;
}

/** GET /web/admin/settings/defaults */
export function buildSystemDefaultsPath(): string {
  return '/web/admin/settings/defaults';
}

/** PATCH /web/admin/settings/reminder-cadence */
export function buildSystemReminderCadencePath(): string {
  return '/web/admin/settings/reminder-cadence';
}

/** PATCH /web/admin/settings/in-channel-reminders */
export function buildSystemInChannelRemindersPath(): string {
  return '/web/admin/settings/in-channel-reminders';
}

/** GET /web/admin/workspaces/:teamId/settings */
export function buildWorkspaceSettingsPath(teamId: string): string {
  return `/web/admin/workspaces/${encodeURIComponent(teamId)}/settings`;
}

/** PATCH /web/admin/workspaces/:teamId/settings/reminder-cadence */
export function buildWorkspaceReminderCadencePath(teamId: string): string {
  return `${buildWorkspaceSettingsPath(teamId)}/reminder-cadence`;
}

/** PATCH /web/admin/workspaces/:teamId/settings/in-channel-reminders */
export function buildWorkspaceInChannelRemindersPath(teamId: string): string {
  return `${buildWorkspaceSettingsPath(teamId)}/in-channel-reminders`;
}

async function readJsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    throw new Error(res.status === 401 ? 'Unauthorized' : text || `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

/**
 * If response is 401, redirect to login and return the response; otherwise return as-is.
 * Caller can use this to separate "redirect on 401" from fetch.
 */
export function handle401Response(response: Response, redirectTo: string = '/'): Response {
  if (response.status === 401) {
    if (typeof window !== 'undefined' && window.location) {
      window.location.href = redirectTo;
    }
    return response;
  }
  return response;
}

/**
 * Fetch with credentials (session cookie). On 401, redirect to login.
 * Uses handle401Response so 401 behavior can be unit-tested separately.
 */
export async function fetchWithAuth(
  path: string,
  options: RequestInit = {},
  deps: { apiBase?: string; fetchFn?: typeof fetch } = {}
): Promise<Response> {
  const apiBase = deps.apiBase ?? getApiBase();
  const fetchFn = deps.fetchFn ?? fetch;
  const url = path.startsWith('http') ? path : `${apiBase}${path}`;
  const method = (options.method ?? 'GET').toUpperCase();
  const readMethod = method === 'GET' || method === 'HEAD';
  const res = await fetchFn(url, {
    ...options,
    credentials: 'include',
    headers: {
      ...(readMethod ? { Accept: 'application/json' } : {}),
      ...options.headers,
    },
  });
  return handle401Response(res);
}

/**
 * GET /web/admin/workspaces — list or search workspaces.
 */
export async function listWorkspaces(
  params: ListWorkspacesParams = {}
): Promise<{ workspaces: import('./types').WorkspaceListItem[] }> {
  const path = buildListWorkspacesPath(params);
  const res = await fetchWithAuth(path);
  if (!res.ok) {
    const text = await res.text();
    throw new Error(res.status === 401 ? 'Unauthorized' : text || `HTTP ${res.status}`);
  }
  return res.json();
}

/**
 * GET /web/admin/workspaces/:teamId — client detail.
 */
export async function getWorkspaceDetail(
  teamId: string
): Promise<import('./types').ClientDetailResponse> {
  const path = buildWorkspaceDetailPath(teamId);
  const res = await fetchWithAuth(path);
  if (!res.ok) {
    const text = await res.text();
    throw new Error(res.status === 401 ? 'Unauthorized' : text || `HTTP ${res.status}`);
  }
  return res.json();
}

/**
 * GET /web/admin/workspaces/:teamId/features/broadcast-mention-tracking
 */
export async function getBroadcastMentionTrackingSettings(
  teamId: string
): Promise<import('./types').BroadcastMentionTrackingSettings> {
  const path = buildBroadcastMentionTrackingPath(teamId);
  const res = await fetchWithAuth(path);
  if (!res.ok) {
    const text = await res.text();
    throw new Error(res.status === 401 ? 'Unauthorized' : text || `HTTP ${res.status}`);
  }
  return res.json();
}

/**
 * PATCH /web/admin/workspaces/:teamId/features/broadcast-mention-tracking
 */
export async function patchBroadcastMentionTrackingSettings(
  teamId: string,
  body: Pick<import('./types').BroadcastMentionTrackingSettings, 'enabled'>
): Promise<import('./types').BroadcastMentionTrackingSettings> {
  const path = buildBroadcastMentionTrackingPath(teamId);
  const res = await fetchWithAuth(path, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return readJsonOrThrow(res);
}

/**
 * GET /web/admin/settings/defaults — system cadence + in-channel (no broadcast).
 */
export async function getSystemDefaults(): Promise<
  import('./types').SystemSettingsDefaultsResponse
> {
  const res = await fetchWithAuth(buildSystemDefaultsPath());
  return readJsonOrThrow(res);
}

/**
 * PATCH /web/admin/settings/reminder-cadence — set minutes or null to reset.
 */
export async function patchSystemReminderCadence(
  cadenceMinutes: number | null
): Promise<import('./types').SettingView<number>> {
  const res = await fetchWithAuth(buildSystemReminderCadencePath(), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cadenceMinutes }),
  });
  return readJsonOrThrow(res);
}

/**
 * PATCH /web/admin/settings/in-channel-reminders — set mode or null to reset.
 */
export async function patchSystemInChannelReminders(
  mode: import('./types').InChannelRemindersMode | null
): Promise<import('./types').SettingView<import('./types').InChannelRemindersMode>> {
  const res = await fetchWithAuth(buildSystemInChannelRemindersPath(), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mode }),
  });
  return readJsonOrThrow(res);
}

/**
 * GET /web/admin/workspaces/:teamId/settings — cadence, in-channel, and broadcast.
 */
export async function getWorkspaceSettings(
  teamId: string
): Promise<import('./types').WorkspaceSettingsResponse> {
  const res = await fetchWithAuth(buildWorkspaceSettingsPath(teamId));
  return readJsonOrThrow(res);
}

/**
 * PATCH /web/admin/workspaces/:teamId/settings/reminder-cadence
 */
export async function patchWorkspaceReminderCadence(
  teamId: string,
  cadenceMinutes: number | null
): Promise<import('./types').SettingView<number>> {
  const res = await fetchWithAuth(buildWorkspaceReminderCadencePath(teamId), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cadenceMinutes }),
  });
  return readJsonOrThrow(res);
}

/**
 * PATCH /web/admin/workspaces/:teamId/settings/in-channel-reminders
 */
export async function patchWorkspaceInChannelReminders(
  teamId: string,
  mode: import('./types').InChannelRemindersMode | null
): Promise<import('./types').SettingView<import('./types').InChannelRemindersMode>> {
  const res = await fetchWithAuth(buildWorkspaceInChannelRemindersPath(teamId), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mode }),
  });
  return readJsonOrThrow(res);
}

/**
 * POST /web/admin/workspaces/:teamId/complimentary-grants — grant complimentary tracks.
 */
export async function postComplimentaryGrant(
  teamId: string,
  tracksGranted: number
): Promise<import('./types').ComplimentaryGrantResponse> {
  const path = buildComplimentaryGrantPath(teamId);
  const res = await fetchWithAuth(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tracksGranted }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(res.status === 401 ? 'Unauthorized' : text || `HTTP ${res.status}`);
  }
  const text = await res.text();
  if (!text) {
    return {};
  }
  return JSON.parse(text) as import('./types').ComplimentaryGrantResponse;
}

export interface AnalyticsAdoptionParams {
  period: import('./types').AnalyticsPeriod;
  granularity: import('./types').AnalyticsGranularity;
}

/**
 * Build path for GET /web/admin/analytics/adoption
 */
export function buildAnalyticsAdoptionPath(params: AnalyticsAdoptionParams): string {
  const search = new URLSearchParams();
  search.set('period', String(params.period));
  search.set('granularity', params.granularity);
  return `/web/admin/analytics/adoption?${search.toString()}`;
}

export interface AnalyticsPaymentsParams {
  period: import('./types').AnalyticsPeriod;
  teamId?: string;
}

export function buildAnalyticsPaymentsPath(params: AnalyticsPaymentsParams): string {
  const search = new URLSearchParams();
  search.set('period', String(params.period));
  if (params.teamId) search.set('teamId', params.teamId);
  return `/web/admin/analytics/payments?${search.toString()}`;
}

export interface AnalyticsUsageParams {
  period: import('./types').AnalyticsPeriod;
}

export function buildAnalyticsUsagePath(params: AnalyticsUsageParams): string {
  const search = new URLSearchParams();
  search.set('period', String(params.period));
  return `/web/admin/analytics/usage?${search.toString()}`;
}

export interface AnalyticsTrendsParams {
  period: import('./types').AnalyticsPeriod;
  granularity: import('./types').AnalyticsGranularity;
}

export function buildAnalyticsTrendsPath(params: AnalyticsTrendsParams): string {
  const search = new URLSearchParams();
  search.set('period', String(params.period));
  search.set('granularity', params.granularity);
  return `/web/admin/analytics/trends?${search.toString()}`;
}

/**
 * GET /web/admin/analytics/adoption
 */
export async function getAnalyticsAdoption(
  params: AnalyticsAdoptionParams
): Promise<import('./types').AdoptionAnalyticsResponse> {
  const path = buildAnalyticsAdoptionPath(params);
  const res = await fetchWithAuth(path);
  if (!res.ok) {
    const text = await res.text();
    throw new Error(res.status === 401 ? 'Unauthorized' : text || `HTTP ${res.status}`);
  }
  return res.json();
}

/**
 * GET /web/admin/analytics/payments
 */
export async function getAnalyticsPayments(
  params: AnalyticsPaymentsParams
): Promise<import('./types').PaymentsAnalyticsResponse> {
  const path = buildAnalyticsPaymentsPath(params);
  const res = await fetchWithAuth(path);
  if (!res.ok) {
    const text = await res.text();
    throw new Error(res.status === 401 ? 'Unauthorized' : text || `HTTP ${res.status}`);
  }
  return res.json();
}

/**
 * GET /web/admin/analytics/usage
 */
export async function getAnalyticsUsage(
  params: AnalyticsUsageParams
): Promise<import('./types').UsageAnalyticsResponse> {
  const path = buildAnalyticsUsagePath(params);
  const res = await fetchWithAuth(path);
  if (!res.ok) {
    const text = await res.text();
    throw new Error(res.status === 401 ? 'Unauthorized' : text || `HTTP ${res.status}`);
  }
  return res.json();
}

/**
 * GET /web/admin/analytics/trends
 */
export async function getAnalyticsTrends(
  params: AnalyticsTrendsParams
): Promise<import('./types').TrendsAnalyticsResponse> {
  const path = buildAnalyticsTrendsPath(params);
  const res = await fetchWithAuth(path);
  if (!res.ok) {
    const text = await res.text();
    throw new Error(res.status === 401 ? 'Unauthorized' : text || `HTTP ${res.status}`);
  }
  return res.json();
}

/**
 * Returns the URL to start Google OAuth (web-backend redirects to Google).
 * Login page should navigate to this URL. Always uses current getApiBase() at call time
 * so the link is correct even when the module loads before globalThis.__VITE_API_BASE is set.
 * If returnTo is provided, it is validated; invalid values fall back to /workspaces.
 */
export function getLoginUrl(returnTo?: string): string {
  const apiBase = getApiBase();
  return getLoginPath(apiBase, returnTo);
}

/**
 * Returns the URL to log out (web-backend clears httpOnly session cookie and redirects to login).
 * Always uses current getApiBase() at call time.
 */
export function getLogoutUrl(apiBase: string = getApiBase()): string {
  return `${apiBase}/web/admin/auth/logout`;
}
